import { useLang } from "../lib/i18n";
import { PROFILE } from "../lib/profile";

export const Hero = () => {
    const { t } = useLang();

    return (
        <section
            id="top"
            className="relative pt-24 md:pt-32 pb-16 md:pb-28 overflow-hidden"
            data-testid="hero-section"
        >
            {/* Background editorial marks */}
            <div className="absolute inset-0 pointer-events-none grain" />
            <div className="absolute -top-10 -right-20 w-[520px] h-[520px] rounded-full bg-warm/20 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 -left-20 w-[380px] h-[380px] rounded-full bg-accentblue/10 blur-3xl pointer-events-none" />
            <div className="absolute top-20 right-4 md:right-12 text-[10rem] md:text-[14rem] font-display leading-none text-navy/[0.04] select-none">
                GV
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center relative">
                {/* LEFT — Portrait (smaller) */}
                <div
                    className="md:col-span-4 relative animate-fade-up"
                    data-testid="hero-portrait-wrap"
                >
                    <div className="relative aspect-[3/4] w-56 sm:w-64 md:w-full max-w-[320px] mx-auto md:mx-0">
                        {/* Cheerful color dot behind */}
                        <div className="absolute -top-5 -left-5 w-16 h-16 rounded-full bg-warm" />
                        <div className="absolute -bottom-5 -right-5 w-20 h-20 rounded-full border-2 border-navy" />

                        <img
                            src={PROFILE.photo}
                            alt="Gilberto Veliz — Desarrollador Web e Inteligencia Artificial en Monterrey"
                            className="relative w-full h-full object-cover contrast-105 shadow-[0_30px_60px_-30px_rgba(10,25,47,0.4)]"
                            data-testid="hero-portrait-img"
                            loading="eager"
                        />

                        {/* Floating caption */}
                        <div className="absolute -bottom-6 -right-2 md:-right-4 bg-cream border border-navy/15 px-4 py-3 shadow-md rotate-[-2deg]">
                            <div className="text-[9px] uppercase tracking-[0.25em] text-navy/50">
                                Est. 2004
                            </div>
                            <div className="mt-0.5 font-display text-base text-navy">
                                Developer · Architect
                            </div>
                        </div>

                        {/* Cheerful sparkle */}
                        <div className="absolute -top-3 right-8 text-warm-600 text-2xl font-display select-none animate-pulse">
                            ✦
                        </div>
                    </div>
                </div>

                {/* RIGHT — Text block */}
                <div
                    className="md:col-span-8 animate-fade-up"
                    style={{ animationDelay: "0.15s" }}
                >
                    <div
                        className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-navy/60 mb-6"
                        data-testid="hero-eyebrow"
                    >
                        <span className="tick" />
                        {t.hero.eyebrow}
                    </div>

                    <h1
                        className="font-display text-[2.75rem] sm:text-6xl lg:text-7xl xl:text-8xl text-navy leading-[0.98] tracking-tight break-words"
                        data-testid="hero-name"
                    >
                        {t.hero.name.split(" ")[0]}{" "}
                        <span className="italic font-light text-warm-600">
                            {t.hero.name.split(" ")[1]}
                        </span>
                    </h1>

                    <div
                        className="mt-5 text-sm text-navy/50 tracking-wider"
                        data-testid="hero-handle"
                    >
                        {t.hero.handle}
                    </div>

                    <div
                        className="mt-8 h-px w-24 bg-warm"
                        aria-hidden="true"
                    />

                    <p
                        className="mt-6 text-lg md:text-xl text-navy/80 max-w-2xl leading-relaxed"
                        data-testid="hero-role"
                    >
                        {t.hero.role}
                    </p>

                    <p
                        className="mt-4 text-base text-navy/65 max-w-2xl leading-relaxed"
                        data-testid="hero-tagline"
                    >
                        {t.hero.tagline}
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <a
                            href="#contact"
                            className="group inline-flex items-center gap-3 bg-navy text-cream px-7 py-3.5 text-sm tracking-wide hover:bg-warm-600 transition-colors"
                            data-testid="hero-cta-primary"
                        >
                            {t.hero.cta}
                            <span className="transition-transform group-hover:translate-x-1">
                                →
                            </span>
                        </a>
                        <a
                            href="#services"
                            className="inline-flex items-center gap-3 border border-navy/30 text-navy px-7 py-3.5 text-sm tracking-wide hover:border-warm hover:bg-warm hover:text-navy transition-all"
                            data-testid="hero-cta-secondary"
                        >
                            {t.hero.ctaSecondary}
                        </a>
                    </div>

                    <div
                        className="mt-10 inline-flex items-center gap-4 px-5 py-3 border border-warm bg-warm-50/60 backdrop-blur-sm rounded-sm"
                        data-testid="hero-years-badge"
                    >
                        <div className="font-display text-3xl text-navy leading-none">
                            20<span className="text-warm-600">+</span>
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em] text-navy/80">
                            {t.hero.yearsBadge.replace(/\+?\s?20\+?\s?/i, "").trim() ||
                                t.hero.yearsBadge}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
